import { Fragment } from 'react'

import StarFilledIcon from '@ant-design/icons/StarFilled'
import StarOutlinedIcon from '@ant-design/icons/StarOutlined'

import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import DialogTitle from '@components/_mui/DialogTitle'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import useUserSetting from '@shared/hooks/src/useUserSetting'
import { useMe } from '@shared/providers/src/MeProvider'
import { TreatmentType, UserSettingName } from '@shared/utils'

import LinearProgress from '@components/LinearProgress'
import CBO from '@components/TableCell/CBO'

import { usePriorityToggle } from './CBOsSupportMapDialog.hooks'

export default function StateCBOsDetailsDialog({ state, cbos = [], open, onClose }) {
  const me = useMe()
  const accessRights = useUserSetting(me, UserSettingName.AccessRights)
  const canToggle = me.superAdmin || accessRights?.value?.cbo_rule_set === true

  const toggle = usePriorityToggle(state)

  const onToggle = (cbo) => toggle.mutate(cbo)

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <LinearProgress loading={toggle.isPending} />
      <DialogTitle onClose={onClose}>State: {state}</DialogTitle>
      <DialogContent dividers>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={80}>ID</TableCell>
                <TableCell>CBO</TableCell>
                <TableCell>Services</TableCell>
                <TableCell width={80} align="center">
                  Priority
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cbos.length === 0 && <Empty />}
              {cbos.length > 0 &&
                cbos.map((cbo) => (
                  <CBORow key={cbo.id} data={cbo} onToggle={canToggle ? () => onToggle(cbo) : undefined} isToggling={toggle.isPending} />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  )
}

function CBORow({ data, isToggling, onToggle }) {
  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2">{data.id}</Typography>
      </TableCell>
      <TableCell>
        <CBO small data={data}>
          <Typography variant="body2">
            <Typography variant="body2" component="span" sx={{ color: 'text.secondary', mr: 1 }}>
              Counties:
            </Typography>
            {data.counties?.join(', ') || 'Fully Covered'}
          </Typography>
        </CBO>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {data.services.length === 0 && '—'}
          {data.services.map((name, index) => (
            <Fragment key={name}>
              {name}
              {name === TreatmentType.OralPrEP && data.prepModalities?.length > 0 && (
                <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                  {` (${data.prepModalities.join(', ')})`}
                </Typography>
              )}
              {index < data.services.length - 1 ? ', ' : ''}
            </Fragment>
          ))}
        </Typography>
      </TableCell>
      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
        {data.isFullyCovered && (
          <IconButton edge="end" color="warning" disabled={isToggling} onClick={onToggle}>
            {data.prioritized ? <StarFilledIcon /> : <StarOutlinedIcon />}
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  )
}

function Empty() {
  return (
    <TableRow sx={{ height: 100 }}>
      <TableCell colSpan={4}>
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 'normal', color: 'text.secondary' }}>
            No data to display
          </Typography>
        </Stack>
      </TableCell>
    </TableRow>
  )
}
